import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Text
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

import { ContractReviewApplyResult as ApplyResult, ContractReviewFinding } from '../../../../models/Entities/Contracts/ContractReview';
import { useContractReviewStyles } from '../ContractReview.Styles';
import { ApplyResultPill } from '../ContractReviewPills/ContractReviewPills.Component';
import { useFieldLabel, useHumanizeText, useValueLabel } from '../ContractReview.Text';

// Resultado de la aplicación (US5, US9): una fila por hallazgo procesado con su resultado y mensaje, los contadores y,
// en NewAddendum, "Ir a la nueva adenda".

export interface ContractReviewApplyResultProps {
  open: boolean;
  result: ApplyResult;
  findings: ContractReviewFinding[];
  onClose: () => void;
  onGoToNewAddendum?: (addendumId: number) => void;
}

export const ContractReviewApplyResult: React.FC<ContractReviewApplyResultProps> = ({ open, result, findings, onClose, onGoToNewAddendum }) => {
  const { t } = useTranslation();
  const styles = useContractReviewStyles();
  const fieldLabel = useFieldLabel();
  const valueLabel = useValueLabel();
  const humanize = useHumanizeText();
  const valuesText = (values: Record<string, unknown>) =>
    Object.entries(values)
      .map(([field, value]) => `${fieldLabel(field)}: ${valueLabel(value)}`)
      .join(' · ');
  const titleOf = (findingId: number, key: string) => findings.find(f => f.id === findingId)?.title ?? key;

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('contractReview.apply.resultTitle')}</DialogTitle>
          <DialogContent>
            <div className={styles.section}>
              <Text className={styles.numbers} data-testid="apply-counters">
                {t('contractReview.apply.counters', {
                  applied: result.appliedCount,
                  conflict: result.conflictCount,
                  skipped: result.skippedCount,
                  failed: result.failedCount
                })}
              </Text>
              <table className={styles.table}>
                <tbody>
                  {result.results.map(item => (
                    <tr key={item.findingId} data-testid={`apply-result-${item.findingId}`}>
                      <th scope="row">
                        <a href={`#finding-title-${item.findingId}`}>{titleOf(item.findingId, item.key)}</a>
                      </th>
                      <td>
                        <ApplyResultPill status={item.applyStatus} />
                      </td>
                      <td>
                        {item.applyStatus === 'Conflict' && <div>{t('contractReview.apply.conflictExplanation')}</div>}
                        {item.message && <div className={styles.muted}>{humanize(item.message)}</div>}
                        {item.applyStatus === 'Conflict' && item.currentValues && (
                          <div className={styles.muted}>{t('contractReview.apply.currentValues', { values: valuesText(item.currentValues) })}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DialogContent>
          <DialogActions>
            {result.createdAddendumId && onGoToNewAddendum && (
              <Button appearance="primary" onClick={() => onGoToNewAddendum(result.createdAddendumId!)}>
                {t('contractReview.apply.goToNewAddendum')}
              </Button>
            )}
            <Button appearance={result.createdAddendumId ? 'secondary' : 'primary'} onClick={onClose}>
              {t('contractReview.apply.close')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ContractReviewApplyResult;
